import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import {
  exportReportDocx,
  exportReportPresentation,
  synthesizeModerationReport,
  updateProcurement,
} from '../../services/procurementService';
import { unifiedStorageService } from '../../services/unifiedStorageService';
import type {
  Assessment,
  Evaluation,
  EvaluatorAssignment,
  Procurement,
  ProcurementSettings,
  Rubric,
  Supplier,
} from '../../types/procurement';

interface Props {
  procurementId: string;
  procurement: Procurement;
  evaluations: Evaluation[];
  rubric: Rubric;
  suppliers: Supplier[];
  assessments: Assessment[];
  evaluatorAssignments?: EvaluatorAssignment[];
  onSaveSettings: (settings: ProcurementSettings) => void;
  onShowSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const FinalReportExport: React.FC<Props> = ({
  procurementId,
  procurement,
  evaluations,
  rubric,
  suppliers,
  assessments,
  evaluatorAssignments = [],
  onSaveSettings,
  onShowSnackbar,
}) => {
  const { account } = useAccount();
  const [localIsSynthesizing, setLocalIsSynthesizing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingPres, setIsExportingPres] = useState(false);
  const [consensusReport, setConsensusReport] = useState<string>('');

  const isSynthesizing = procurement.reportGenerationStatus === 'GENERATING' || localIsSynthesizing;

  useEffect(() => {
    const decryptReport = async () => {
      if (procurement.consensusReport) {
        setConsensusReport(procurement.consensusReport);
      }
    };
    decryptReport();
  }, [procurement.consensusReport]);

  const getPayloadData = () => {
    return {
      rubric: rubric.criteria,
      suppliers: suppliers.map((s) => ({
        id: s.id,
        name: s.name,
        status: s.status,
        documents: s.documents,
      })),
      evaluations,
      assessments,
    };
  };

  const handleSynthesize = async () => {
    setLocalIsSynthesizing(true);
    try {
      await updateProcurement(procurementId, { reportGenerationStatus: 'GENERATING' });

      const result = await synthesizeModerationReport(
        procurementId,
        account?.clientId || '',
        account?.uid || '',
        getPayloadData(),
        currentSettings
      );
      if (result.success && result.report_text) {
        setConsensusReport(result.report_text);

        const updateData: Partial<Procurement> = {
          consensusReport: result.report_text,
          reportGenerationStatus: 'COMPLETED',
        };

        await updateProcurement(procurementId, updateData);
        onShowSnackbar?.('Final report generated successfully.', 'success');
      } else {
        await updateProcurement(procurementId, { reportGenerationStatus: 'FAILED' });
        onShowSnackbar?.(result.error || 'Failed to generate report.', 'error');
      }
    } catch (err: unknown) {
      const e = err as Error;
      await updateProcurement(procurementId, { reportGenerationStatus: 'FAILED' }).catch(() => {
        // Ignore fallback update errors
      });
      onShowSnackbar?.(e.message || 'Report generation failed.', 'error');
    } finally {
      setLocalIsSynthesizing(false);
    }
  };

  const handleExportDocx = async () => {
    if (!consensusReport) {
      return;
    }
    setIsExporting(true);
    try {
      const result = await exportReportDocx(procurementId, consensusReport);
      if (result.success) {
        if (result.download_url) {
          window.open(result.download_url, '_blank');
          onShowSnackbar?.('Exported DOCX successfully.', 'success');
        } else if (result.remote_path) {
          await unifiedStorageService.triggerBrowserDownload(result.remote_path);
          onShowSnackbar?.('Exported DOCX successfully.', 'success');
        } else {
          throw new Error('No download URL or path returned from server.');
        }
      } else {
        onShowSnackbar?.(result.error || 'Failed to export DOCX.', 'error');
      }
    } catch (err: unknown) {
      const e = err as Error;
      onShowSnackbar?.(e.message || 'DOCX export failed.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPresentation = async (format: 'pptx' | 'html') => {
    setIsExportingPres(true);
    try {
      const result = await exportReportPresentation(procurementId, getPayloadData());
      if (result.success) {
        const urlToOpen = format === 'pptx' ? result.pptx_download_url : result.html_download_url;
        // The service might return the raw 'pptx_url' or 'html_url' starting with gs://
        const fallbackPath = format === 'pptx' ? result.pptx_url : result.html_url;

        if (urlToOpen) {
          window.open(urlToOpen, '_blank');
          onShowSnackbar?.(
            `Exported ${format.toUpperCase()} Presentation successfully.`,
            'success'
          );
        } else if (fallbackPath) {
          await unifiedStorageService.triggerBrowserDownload(fallbackPath);
          onShowSnackbar?.(
            `Exported ${format.toUpperCase()} Presentation successfully.`,
            'success'
          );
        } else {
          onShowSnackbar?.(`Failed to get URL for ${format.toUpperCase()} Presentation.`, 'error');
        }
      } else {
        onShowSnackbar?.(result.error || 'Failed to export Presentation.', 'error');
      }
    } catch (err: unknown) {
      const e = err as Error;
      onShowSnackbar?.(e.message || 'Presentation export failed.', 'error');
    } finally {
      setIsExportingPres(false);
    }
  };

  const allEvaluatorsSubmitted =
    evaluatorAssignments.length > 0
      ? evaluatorAssignments.every((a) =>
          evaluations.some(
            (e) =>
              e.evaluatorId === a.userId &&
              e.supplierId === a.supplierId &&
              e.status === 'SUBMITTED'
          )
        )
      : true;

  const defaultSettings: ProcurementSettings = {
    compliance: { strictness: 'MEDIUM', customKeywords: [] },
    nliRedTeam: { enabled: true, confidenceThreshold: 'MEDIUM' },
    evaluation: { useFuzzyScoring: true, enabled: true },
    moderation: { reportFormat: 'EXECUTIVE_SUMMARY', tone: 'FORMAL' },
  };

  const currentSettings = procurement?.settings || defaultSettings;

  const handleUpdateSetting = (field: string, value: string) => {
    onSaveSettings({
      ...currentSettings,
      moderation: {
        ...currentSettings.moderation,
        [field]: value,
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Final Report & Export
        </Typography>
        <Box component="ul" sx={{ mt: 0, pl: 2, color: 'text.secondary', '& li': { mb: 0.5 } }}>
          <Typography component="li" variant="body2">
            Compile your procurement event into a comprehensive executive report.
          </Typography>
          <Typography component="li" variant="body2">
            Export the report to formal documents or presentations.
          </Typography>
          <Typography component="li" variant="body2">
            Ensure all evaluators have submitted their scores before generating the report.
          </Typography>
        </Box>
      </Box>

      {/* Settings Card */}
      <Card>
        <CardHeader sx={{ bgcolor: 'action.hover' }} title="AI Synthesis Settings" />
        <Divider />
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="body1" gutterBottom>
                Consensus & Moderation
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Controls the formatting of the final consensus justification report.
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 12, md: 5 }}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                select
                fullWidth
                size="small"
                label="Report Format"
                value={currentSettings.moderation.reportFormat}
                onChange={(e) => handleUpdateSetting('reportFormat', e.target.value)}
              >
                <MenuItem value="BULLET_POINTS">Bullet Points</MenuItem>
                <MenuItem value="EXECUTIVE_SUMMARY">Executive Summary</MenuItem>
                <MenuItem value="DETAILED_PARAGRAPHS">Detailed Paragraphs</MenuItem>
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Report Tone"
                value={currentSettings.moderation.tone}
                onChange={(e) => handleUpdateSetting('tone', e.target.value)}
              >
                <MenuItem value="FORMAL">Formal & Audit-Ready</MenuItem>
                <MenuItem value="CONVERSATIONAL">Conversational</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box>
        <Typography variant="h6" gutterBottom>
          1. Generate Consensus Report
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Instruct the AI to evaluate all submissions and synthesize a comprehensive report based on
          the rubric, compliance checks, and human evaluations.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            isSynthesizing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
          }
          onClick={handleSynthesize}
          disabled={isSynthesizing || evaluations.length === 0 || !allEvaluatorsSubmitted}
          data-testid="synthesize-btn"
        >
          {isSynthesizing ? 'Generating Report...' : 'Generate Report'}
        </Button>
        {!allEvaluatorsSubmitted && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            All assigned evaluators must submit their scores before the report can be generated.
          </Typography>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="h6" gutterBottom>
          2. Export to Document (DOCX)
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Download the generated consensus report as a fully formatted Microsoft Word document. You
          must generate the report in Step 1 before you can export.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExportDocx}
          disabled={!consensusReport || isExporting}
          startIcon={
            isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
          }
        >
          {isExporting ? 'Exporting...' : 'Export to DOCX'}
        </Button>
      </Box>

      <Divider />

      <Box>
        <Typography variant="h6" gutterBottom>
          3. Convert to Presentation (Coming Soon)
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Generate a slide deck summarizing the procurement outcome. This feature is currently
          disabled for maintenance.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={true}
            onClick={() => handleExportPresentation('pptx')}
            startIcon={
              isExportingPres ? <CircularProgress size={16} color="inherit" /> : <SlideshowIcon />
            }
          >
            {isExportingPres ? 'Converting...' : 'Export PPTX'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={true}
            onClick={() => handleExportPresentation('html')}
            startIcon={
              isExportingPres ? <CircularProgress size={16} color="inherit" /> : <SlideshowIcon />
            }
          >
            {isExportingPres ? 'Converting...' : 'Export HTML'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FinalReportExport;
